import React from 'react'

import { ANALYTICS_CATEGORIES, ID, useAnalyticsClient } from '~/service'

import {
  ActionProps,
  ActionRef,
  ChatAction,
  PureChatWithDelay,
  PureChatWithDelayProps,
} from '~/components'

const category = ANALYTICS_CATEGORIES.CONTACT
const action = 'Toggle Chat'

interface ChatButtonProps extends ActionProps {
  isOpen?: boolean
}

export const ChatButton = React.forwardRef(
  ({ isOpen, label, ...rest }: ChatButtonProps, ref: React.Ref<ActionRef>) => {
    return (
      <ChatAction
        data-testid="ChatButton"
        button
        transparent
        size="m"
        category={category}
        action={action}
        label={`${isOpen ? 'Close' : 'Open'} ${label}`}
        {...rest}
        ref={ref}
      />
    )
  }
)

interface PureChatProps extends ChatButtonProps {
  pureChatId: ID
  client?: PureChatWithDelayProps['client']
  delay?: number
}

/**
 * Render chat using the PureChat API.
 */
export function PureChat({
  pureChatId,
  client,
  delay,
  ...rest
}: PureChatProps) {
  const label = 'Pure Chat'
  const [isOpen, setIsOpen] = React.useState(false)
  const [isReady, setIsReady] = React.useState(false)

  const analytics = useAnalyticsClient()

  const onToggle = () => setIsOpen((o) => !o)
  const onCloseEvent = () => {
    analytics.trackEvent(category, action, `Close ${label}`)
    setIsOpen(false)
  }
  const onReady = () => setIsReady(true)

  return (
    <>
      {isReady && (
        <ChatButton
          onClick={onToggle}
          isOpen={isOpen}
          label={label}
          {...rest}
        />
      )}
      <PureChatWithDelay
        clientId={pureChatId}
        isOpen={isOpen}
        onClose={onCloseEvent}
        onReady={onReady}
        client={client}
        delay={delay}
      />
    </>
  )
}

interface StandardChatProps extends ChatButtonProps {
  chatURL?: string
}

/**
 * Render chat as an iframe to a 3rd party chat page.
 */
export function StandardChat({ chatURL, ...rest }: StandardChatProps) {
  const label = 'Standard Chat'

  return (
    <>
      <ChatButton
        button={false}
        feel="button"
        href={chatURL}
        blank
        label={label}
        {...rest}
      />
    </>
  )
}

interface ChatProps extends ChatButtonProps {
  /**
   * The URL of the chat site to open when not using
   * the PureChat API.
   */
  chatURL?: string
  /**
   * The id of the PureChat client if the community uses
   * PureChat. If this is not set, then the standard chat is used.
   */
  pureChatId?: ID
  /**
   * The delay in milliseconds before loading the PureChat API,
   * if using PureChat.
   */
  pureChatDelay?: number
  pureChatClient?: PureChatWithDelayProps['client']
}

export function Chat({
  chatURL,
  pureChatId,
  pureChatClient,
  pureChatDelay,
  ...rest
}: ChatProps) {
  return !pureChatId ? (
    <StandardChat chatURL={chatURL} {...rest} />
  ) : (
    <PureChat
      pureChatId={pureChatId!}
      client={pureChatClient}
      delay={pureChatDelay}
      {...rest}
    />
  )
}
